/**
 * HeyKoala embeddable voice widget — single-file vanilla TS bundle.
 *
 * Customer page loads:
 *   <script async src=".../widget.js" data-public-key="wd_xxx"></script>
 *
 * The programmatic API — window.HeyKoalaVoice (.start / .getToken + events +
 * .mute/.unmute/.sendText/.end) — is ALWAYS available, in BOTH modes. A host
 * page can drive a call from its own button regardless of the deployment mode.
 *
 * The per-deployment `mode` (WebDeployment.mode, surfaced in the public
 * /config/ response) ONLY decides whether the built-in launcher UI auto-mounts:
 *   • "widget"   — this file also renders the launcher + shadow-DOM call UI
 *                  (default). The window.HeyKoalaVoice API still works too.
 *   • "headless" — the built-in launcher is NOT rendered; the host page uses
 *                  the window.HeyKoalaVoice API with its own button + DOM.
 *
 * The LiveKit machinery lives in ./core (VoiceCall); this file is the UI layer.
 * No React, no @livekit/components-react — mounts under a shadow root so host
 * page styles don't leak in.
 */
import { ConnectionState } from "livekit-client"

import {
  VoiceCall,
  bypassHeaders,
  joinUrl,
  provisionCall,
  type AgentState,
  type ProvisionResult,
  type VoiceCallOptions,
} from "./core"
import styles from "./styles.css?inline"

// ─── Types & config ─────────────────────────────────────────────────────────

interface MountConfig {
  publicKey: string
  apiBase: string
}

interface WidgetConfig {
  name: string
  branding: {
    primary_color: string
    position: "bottom-right" | "bottom-left"
    button_label: string
  }
  /** "widget" renders the UI; "headless" skips it (host drives the call). */
  mode?: "widget" | "headless"
  // When true, show a text input during the call so the visitor can type an
  // exact answer in addition to speaking. Defaults on if the field is absent.
  show_text_input?: boolean
  // Proactive greeting: shown as a teaser bubble next to the closed launcher
  // and spoken (greeting_audio_url) on the visitor's first interaction.
  greeting_message?: string
  greeting_audio_url?: string
}

/** Options accepted by the headless SDK entry point. */
export interface StartOptions {
  publicKey: string
  /** API origin. Defaults to the loading script's origin, then page origin. */
  apiBase?: string
  sessionId?: string
  autoMic?: boolean
}

declare global {
  interface Window {
    HeyKoalaWidget?: { mount: (config: MountConfig) => void }
    HeyKoalaVoice?: {
      /** Start a managed call and get a handle (events + controls + .room). */
      start: (options: StartOptions) => VoiceCall
      /**
       * Provision a call WITHOUT connecting — returns the LiveKit token +
       * serverUrl so you can drive the room yourself with the LiveKit client
       * SDK (livekit-client / @livekit/components-react).
       */
      getToken: (options: StartOptions) => Promise<ProvisionResult>
    }
  }
}

// ─── API helpers ────────────────────────────────────────────────────────────

async function fetchWidgetConfig(
  apiBase: string,
  publicKey: string
): Promise<WidgetConfig> {
  const res = await fetch(
    joinUrl(apiBase, `api/v1/widgets/public/${publicKey}/config/`),
    {
      method: "GET",
      credentials: "omit",
      headers: { Accept: "application/json", ...bypassHeaders(apiBase) },
    }
  )
  if (!res.ok) throw new Error(`Widget config request failed (${res.status})`)
  return (await res.json()) as WidgetConfig
}

// ─── DOM helpers ────────────────────────────────────────────────────────────

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: {
    className?: string
    text?: string
    html?: string
    attrs?: Record<string, string>
    style?: Partial<CSSStyleDeclaration> & Record<string, string>
  } = {}
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag)
  if (opts.className) node.className = opts.className
  if (opts.text != null) node.textContent = opts.text
  if (opts.html != null) node.innerHTML = opts.html
  if (opts.attrs) {
    for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v)
  }
  if (opts.style) {
    for (const [k, v] of Object.entries(opts.style)) {
      ;(node.style as unknown as Record<string, string>)[k] = v as string
    }
  }
  return node
}

const PHONE_ICON_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.94.37 1.86.73 2.73a2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.87.36 1.79.6 2.73.73A2 2 0 0 1 22 16.92z"/>
  </svg>`

const CHECK_ICON_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12"/>
  </svg>`

const MIC_ICON_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>`

const SEND_ICON_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M12 19V5"/>
    <path d="m5 12 7-7 7 7"/>
  </svg>`

const MIC_OFF_ICON_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <line x1="2" y1="2" x2="22" y2="22"/>
    <path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/>
    <path d="M5 10v2a7 7 0 0 0 12 5"/>
    <path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/>
    <path d="M9 9v3a3 3 0 0 0 5.12 2.12"/>
    <line x1="12" y1="19" x2="12" y2="23"/>
    <line x1="8" y1="23" x2="16" y2="23"/>
  </svg>`

// ─── Voice stage state mapping (widget UI only) ─────────────────────────────

function stageClass(agent: AgentState, conn: ConnectionState): string {
  if (
    conn === ConnectionState.Disconnected ||
    conn === ConnectionState.Reconnecting
  )
    return "ended"
  if (conn === ConnectionState.Connecting) return "connecting"
  if (agent === "speaking") return "speaking"
  if (agent === "thinking" || agent === "initializing") return "thinking"
  if (agent === "failed" || agent === "disconnected") return "ended"
  return "listening"
}

function statusLabel(
  agent: AgentState,
  conn: ConnectionState,
  hasAgent: boolean
): string {
  if (conn === ConnectionState.Connecting) return "Connecting…"
  if (conn === ConnectionState.Reconnecting) return "Reconnecting…"
  if (conn === ConnectionState.Disconnected) return "Disconnected"
  if (!hasAgent) return "Waiting for agent…"
  if (agent === "speaking") return "Speaking"
  if (agent === "listening") return "Listening"
  if (agent === "thinking") return "Thinking"
  return "Connected"
}

// ─── Dialog (one live instance at a time) ───────────────────────────────────

interface DialogCtx {
  root: HTMLElement
  config: WidgetConfig
  apiBase: string
  publicKey: string
  onClose: () => void
}

function openDialog(ctx: DialogCtx): () => void {
  const { config, apiBase, publicKey, onClose } = ctx
  const primaryColor = config.branding.primary_color

  // ── panel skeleton ────────────────────────────────────────────────────
  const panel = el("div", {
    className: "hk-panel",
    attrs: { role: "dialog", "aria-label": config.name },
  })

  const header = el("div", {
    className: "hk-header",
    style: { backgroundColor: primaryColor },
  })
  header.appendChild(el("span", { className: "hk-title", text: config.name }))
  const closeBtn = el("button", {
    className: "hk-close",
    text: "×",
    attrs: { type: "button", "aria-label": "Close" },
  })
  header.appendChild(closeBtn)
  panel.appendChild(header)

  const body = el("div", { className: "hk-body" })
  panel.appendChild(body)

  const footer = el("div", { className: "hk-footer" })
  footer.innerHTML =
    'Powered by <a class="hk-footer-link" href="https://heykoala.ai" target="_blank" rel="noopener noreferrer">HeyKoala</a>'
  panel.appendChild(footer)

  ctx.root.appendChild(panel)

  // ── loading state ─────────────────────────────────────────────────────
  body.innerHTML = `
    <div class="hk-spinner" aria-hidden="true"></div>
    <div class="hk-status">Connecting…</div>`

  // ── shared teardown ───────────────────────────────────────────────────
  let call: VoiceCall | null = null
  let cleanupTimeout: number | null = null
  let disposed = false

  closeBtn.addEventListener("click", () => dispose())

  function dispose() {
    if (disposed) return
    disposed = true
    if (cleanupTimeout != null) window.clearTimeout(cleanupTimeout)
    if (call) {
      void call.end()
      call = null
    }
    panel.remove()
    onClose()
  }

  // ── gate on mic permission, then connect ──────────────────────────────
  void startWithMicGate()

  async function startWithMicGate() {
    // Probe permission state quietly if the API is available — lets us skip
    // the gate UI when the user has already granted access on a prior visit.
    let state: PermissionState | "unknown" = "unknown"
    try {
      const perms = (
        navigator as Navigator & {
          permissions?: {
            query: (d: { name: string }) => Promise<PermissionStatus>
          }
        }
      ).permissions
      if (perms?.query) {
        const status = await perms.query({
          name: "microphone" as PermissionName,
        })
        state = status.state
      }
    } catch {
      // Some browsers (Safari, older Firefox) don't expose the microphone
      // permission descriptor — fall through to the explicit prompt path.
    }
    if (disposed) return

    if (state === "granted") {
      await proceedToCall()
      return
    }

    renderPermissionGate(state === "denied" ? "denied" : "prompt")
  }

  async function requestMicAccess(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      // We don't keep this stream — LiveKit will open its own track. Release
      // it immediately so the mic indicator doesn't double up.
      stream.getTracks().forEach(t => t.stop())
      return true
    } catch {
      return false
    }
  }

  async function proceedToCall() {
    if (disposed) return

    // renderCallUI creates the hidden audio container the agent's voice plays
    // into — build the UI first, then hand that container to the call core.
    renderCallUI()
    if (!audioContainer) return

    call = new VoiceCall({ apiBase, publicKey, audioContainer })
    call.on("status", () => refreshUI())
    call.on("agentstate", () => refreshUI())
    call.on("transcript", t => handleTranscript(t.text, t.fromSelf))
    call.on("error", err => {
      if (!disposed) renderError(err.message)
    })

    void call.start()
  }

  function renderPermissionGate(reason: "prompt" | "denied") {
    body.innerHTML = ""
    const wrap = el("div", {
      className: "hk-permission",
      style: { ["--hk-color" as string]: primaryColor },
    })

    const icon = el("div", {
      className: "hk-permission-icon",
      html: MIC_ICON_SVG,
    })
    wrap.appendChild(icon)

    wrap.appendChild(
      el("div", {
        className: "hk-permission-title",
        text:
          reason === "denied"
            ? "Microphone is blocked"
            : "Microphone access needed",
      })
    )
    wrap.appendChild(
      el("div", {
        className: "hk-permission-body",
        text:
          reason === "denied"
            ? "Please allow microphone access in your browser settings, then try again."
            : "We use your microphone to talk to the assistant. Please grant access to continue.",
      })
    )

    const btn = el("button", {
      className: "hk-control-primary",
      text: reason === "denied" ? "Try again" : "Allow microphone",
      attrs: { type: "button" },
    }) as HTMLButtonElement
    btn.addEventListener("click", async () => {
      btn.setAttribute("disabled", "true")
      btn.textContent = "Waiting for permission…"
      const ok = await requestMicAccess()
      if (disposed) return
      if (ok) {
        await proceedToCall()
      } else {
        renderPermissionGate("denied")
      }
    })
    wrap.appendChild(btn)
    body.appendChild(wrap)
  }

  // ── render: error ─────────────────────────────────────────────────────
  function renderError(message: string) {
    body.innerHTML = ""
    body.appendChild(el("div", { className: "hk-status error", text: message }))
    const retry = el("button", {
      className: "hk-control",
      text: "Close",
      attrs: { type: "button" },
    })
    retry.addEventListener("click", () => dispose())
    body.appendChild(retry)
  }

  // ── render: call UI ───────────────────────────────────────────────────
  let stageEl: HTMLDivElement | null = null
  let statusEl: HTMLDivElement | null = null
  let muteBtn: HTMLButtonElement | null = null
  let endBtn: HTMLButtonElement | null = null
  let audioContainer: HTMLDivElement | null = null

  // Typed-answer field — shown for the whole call when enabled in widget config.
  const textInputEnabled = config.show_text_input !== false
  const TYPED_PLACEHOLDER = "Type your answer, or just speak…"
  let inputRow: HTMLDivElement | null = null
  let answerInput: HTMLInputElement | null = null

  // Live conversation transcript (chat bubbles).
  let transcriptEl: HTMLDivElement | null = null
  // De-dupe identical turns that arrive via both transcription delivery paths
  // (text stream + legacy event), keyed by sender+text within a short window.
  const recentMsgKeys = new Map<string, number>()

  function renderCallUI() {
    body.innerHTML = ""

    audioContainer = el("div", {
      style: { display: "none" },
      attrs: { "aria-hidden": "true" },
    }) as HTMLDivElement
    body.appendChild(audioContainer)

    stageEl = el("div", {
      className: "hk-stage connecting",
      attrs: { role: "img", "aria-label": "Assistant connecting" },
      style: { ["--hk-color" as string]: primaryColor },
    }) as HTMLDivElement
    stageEl.appendChild(el("span", { className: "hk-ring" }))
    stageEl.appendChild(el("span", { className: "hk-ring" }))
    stageEl.appendChild(el("span", { className: "hk-ring" }))
    stageEl.appendChild(el("div", { className: "hk-orb" }))
    const thinkDots = el("div", {
      className: "hk-think-dots",
      attrs: { "aria-hidden": "true" },
      html: "<span></span><span></span><span></span>",
    })
    stageEl.appendChild(thinkDots)
    body.appendChild(stageEl)

    statusEl = el("div", {
      className: "hk-status",
      text: "Connecting…",
    }) as HTMLDivElement
    body.appendChild(statusEl)

    // Live transcript — chat bubbles for each finalized turn (agent + visitor).
    transcriptEl = el("div", {
      className: "hk-transcript",
      attrs: {
        role: "log",
        "aria-live": "polite",
        "aria-label": "Conversation transcript",
      },
    }) as HTMLDivElement
    transcriptEl.appendChild(
      el("div", {
        className: "hk-transcript-empty",
        text: "Your conversation will appear here.",
      })
    )
    body.appendChild(transcriptEl)

    // Typed-answer field — shown for the whole call (when enabled in config) so
    // the visitor can type an exact answer (no STT spelling errors). The mic
    // stays live, so they can still just say it.
    if (textInputEnabled) {
      inputRow = el("div", {
        className: "hk-input-row",
        style: { display: "none", ["--hk-color" as string]: primaryColor },
      }) as HTMLDivElement
      answerInput = el("input", {
        className: "hk-input",
        attrs: {
          type: "text",
          "aria-label": "Type your answer",
          placeholder: TYPED_PLACEHOLDER,
          autocomplete: "off",
        },
      }) as HTMLInputElement
      const sendBtn = el("button", {
        className: "hk-input-send",
        html: SEND_ICON_SVG,
        attrs: { type: "button", "aria-label": "Send answer" },
        style: { backgroundColor: primaryColor },
      }) as HTMLButtonElement
      answerInput.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.key === "Enter") {
          e.preventDefault()
          submitTypedAnswer()
        }
      })
      sendBtn.addEventListener("click", submitTypedAnswer)
      inputRow.appendChild(answerInput)
      inputRow.appendChild(sendBtn)
    }

    // Action bar: [ composer (grows) ] [mute] [end] — icon-only controls.
    const actionbar = el("div", { className: "hk-actionbar" })
    if (inputRow) actionbar.appendChild(inputRow)

    muteBtn = el("button", {
      className: "hk-icon-btn",
      html: MIC_ICON_SVG,
      attrs: { type: "button", "aria-label": "Mute", disabled: "true" },
    }) as HTMLButtonElement
    muteBtn.addEventListener("click", toggleMute)

    endBtn = el("button", {
      className: "hk-icon-btn danger",
      html: PHONE_ICON_SVG,
      attrs: { type: "button", "aria-label": "End call" },
    }) as HTMLButtonElement
    endBtn.addEventListener("click", () => dispose())

    actionbar.appendChild(muteBtn)
    actionbar.appendChild(endBtn)
    body.appendChild(actionbar)

    // Seed the conversation with the greeting as the agent's first bubble. If
    // the agent then speaks the same opening line, addMessage's dedupe drops it.
    const greeting = config.greeting_message?.trim()
    if (greeting) addMessage(greeting, false)
  }

  function refreshUI() {
    if (!call || !stageEl || !statusEl || !muteBtn) return

    const agentState = call.agentState
    const connState = call.connState

    if (connState === ConnectionState.Disconnected && call.hadConnection) {
      // Call-ended view, then auto-close.
      body.innerHTML = ""
      const endedStage = el("div", {
        className: "hk-stage ended",
        style: { ["--hk-color" as string]: primaryColor },
      })
      endedStage.appendChild(el("div", { className: "hk-orb" }))
      body.appendChild(endedStage)
      const badge = el("div", { className: "hk-ended-badge" })
      badge.innerHTML = CHECK_ICON_SVG + "<span>Call ended</span>"
      body.appendChild(badge)
      if (cleanupTimeout == null) {
        cleanupTimeout = window.setTimeout(() => dispose(), 1200)
      }
      return
    }

    const cls = stageClass(agentState, connState)
    stageEl.className = `hk-stage ${cls}`
    stageEl.setAttribute("aria-label", `Assistant ${cls}`)
    statusEl.textContent = statusLabel(agentState, connState, call.hasAgent)
    muteBtn.innerHTML = call.muted ? MIC_OFF_ICON_SVG : MIC_ICON_SVG
    muteBtn.setAttribute("aria-label", call.muted ? "Unmute" : "Mute")
    muteBtn.classList.toggle("active", call.muted)
    if (connState === ConnectionState.Connected)
      muteBtn.removeAttribute("disabled")
    else muteBtn.setAttribute("disabled", "true")

    updateInputVisibility()
  }

  async function toggleMute() {
    if (!call) return
    await call.setMuted(!call.muted)
    refreshUI()
  }

  // ── typed answers ─────────────────────────────────────────────────────
  // The field stays visible for the whole call (it's just an alternative to
  // speaking). We use the transcript only to keep the placeholder in sync with
  // the agent's latest question and to focus the field when a question is asked.
  function updateInputVisibility() {
    if (!inputRow) return
    inputRow.style.display =
      call?.connState === ConnectionState.Connected ? "flex" : "none"
  }

  function submitTypedAnswer() {
    if (!call || !answerInput) return
    const value = answerInput.value.trim()
    if (!value) return
    // Deliver as a user text turn on the agent's text-input topic. The agent
    // treats it like a spoken turn but with exact spelling (no STT errors).
    call.sendText(value)
    // Typed text isn't STT-transcribed, so add it to the transcript ourselves.
    addMessage(value, true)
    answerInput.value = ""
  }

  // Append a chat bubble to the transcript and keep it scrolled to the latest.
  function addMessage(text: string, fromSelf: boolean) {
    if (!transcriptEl) return
    const t = text.trim()
    if (!t) return
    // Drop duplicates of the same turn arriving via both delivery paths.
    const key = `${fromSelf ? "u" : "a"}|${t.toLowerCase()}`
    const now = Date.now()
    const prev = recentMsgKeys.get(key)
    if (prev != null && now - prev < 5000) return
    recentMsgKeys.set(key, now)
    transcriptEl.querySelector(".hk-transcript-empty")?.remove()
    const row = el("div", {
      className: `hk-msg ${fromSelf ? "user" : "agent"}`,
    })
    const bubble = el("div", { className: "hk-bubble", text: t })
    if (fromSelf) bubble.style.backgroundColor = primaryColor
    row.appendChild(bubble)
    transcriptEl.appendChild(row)
    transcriptEl.scrollTop = transcriptEl.scrollHeight
  }

  // Each finalized transcript segment → a chat bubble.
  function handleTranscript(text: string, fromSelf: boolean) {
    addMessage(text, fromSelf)
    if (!answerInput) return
    if (fromSelf) {
      answerInput.placeholder = TYPED_PLACEHOLDER
      return
    }
    // Agent asked a question — surface it as the placeholder and focus the
    // field (unless the visitor is already typing).
    if (/[?？]["')\]]?\s*$/.test(text)) {
      answerInput.placeholder = text
      if (
        inputRow &&
        inputRow.style.display !== "none" &&
        !answerInput.value.trim()
      ) {
        answerInput.focus()
      }
    }
  }

  return dispose
}

// ─── Root widget (launcher + dialog toggle) ─────────────────────────────────

function mountWidget(
  shadowRoot: ShadowRoot,
  cfg: WidgetConfig,
  mc: MountConfig
) {
  const root = el("div", {
    className: `hk-root position-${cfg.branding.position}`,
  })
  shadowRoot.appendChild(root)

  const launcher = el("button", {
    className: "hk-launcher attention",
    attrs: { type: "button", "aria-label": cfg.branding.button_label },
    style: { backgroundColor: cfg.branding.primary_color },
  }) as HTMLButtonElement
  launcher.innerHTML =
    PHONE_ICON_SVG + `<span>${escapeHtml(cfg.branding.button_label)}</span>`
  root.appendChild(launcher)

  let dispose: (() => void) | null = null
  let dismissTeaser: () => void = () => {}

  function openWidget() {
    if (dispose) {
      dispose()
      return
    }
    launcher.classList.remove("attention")
    dismissTeaser()
    dispose = openDialog({
      root,
      config: cfg,
      apiBase: mc.apiBase,
      publicKey: mc.publicKey,
      onClose: () => {
        dispose = null
        launcher.classList.add("attention")
      },
    })
  }

  launcher.addEventListener("click", openWidget)

  // Proactive greeting: a teaser bubble next to the closed launcher plus the
  // spoken greeting played at the first browser-allowed moment.
  dismissTeaser = setupGreeting(cfg, root, openWidget)
}

/**
 * Render the proactive greeting (if configured): a teaser chat bubble next to
 * the closed launcher, and the spoken greeting audio.
 *
 * Browser autoplay policy blocks audio until the visitor interacts with the
 * page, so we (a) proactively request mic permission on load — the prompt
 * response is itself an interaction — and (b) keep a one-time gesture listener
 * as a fallback. The audio plays at most once, at the earliest allowed moment.
 *
 * Returns a function that dismisses the teaser (called when the widget opens).
 */
function setupGreeting(
  cfg: WidgetConfig,
  root: HTMLElement,
  openWidget: () => void
): () => void {
  const message = cfg.greeting_message?.trim()
  if (!message) return () => {}

  const teaser = el("div", { className: "hk-teaser" })
  const teaserText = el("div", { className: "hk-teaser-text", text: message })
  const teaserClose = el("button", {
    className: "hk-teaser-close",
    text: "×",
    attrs: { type: "button", "aria-label": "Dismiss greeting" },
  }) as HTMLButtonElement
  teaser.appendChild(teaserText)
  teaser.appendChild(teaserClose)
  root.appendChild(teaser)

  let dismissed = false
  const dismiss = () => {
    if (dismissed) return
    dismissed = true
    teaser.remove()
  }
  teaserClose.addEventListener("click", e => {
    e.stopPropagation()
    dismiss()
  })
  teaserText.addEventListener("click", () => openWidget())

  const audioUrl = cfg.greeting_audio_url
  if (audioUrl) {
    let played = false
    const audio = new Audio(audioUrl)
    audio.preload = "auto"

    const onGesture = () => attemptPlay()
    const stopListening = () => {
      document.removeEventListener("pointerdown", onGesture, true)
      document.removeEventListener("keydown", onGesture, true)
    }
    const attemptPlay = () => {
      if (played) return
      audio
        .play()
        .then(() => {
          played = true
          stopListening()
        })
        .catch(() => {
          // Still blocked (no qualifying gesture yet) — a later gesture retries.
        })
    }

    document.addEventListener("pointerdown", onGesture, true)
    document.addEventListener("keydown", onGesture, true)

    // Ask for mic permission as the page opens; the prompt response unblocks
    // autoplay. Best-effort — we play regardless of grant/deny.
    navigator.mediaDevices
      ?.getUserMedia?.({ audio: true })
      .then(stream => stream.getTracks().forEach(t => t.stop()))
      .catch(() => {})
      .finally(() => attemptPlay())
  }

  return dismiss
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    c =>
      (
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        }) as Record<string, string>
      )[c]
  )
}

// ─── Bootstrap ──────────────────────────────────────────────────────────────

function readScriptConfig(): MountConfig | null {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    "script[data-public-key]"
  )
  const script = scripts[scripts.length - 1]
  if (!script) return null

  const publicKey = script.dataset.publicKey
  if (!publicKey) return null

  return { publicKey, apiBase: resolveApiBase(script.dataset.apiBase) }
}

/**
 * Resolve the API origin: explicit data-api-base wins, else the origin of the
 * loaded widget script, else the page origin.
 */
function resolveApiBase(explicit?: string): string {
  if (explicit) return explicit
  const script = document.querySelector<HTMLScriptElement>(
    "script[data-public-key]"
  )
  if (script?.src) {
    try {
      return new URL(script.src, window.location.href).origin
    } catch {
      // fall through
    }
  }
  return window.location.origin
}

// Inject Satoshi (the app's brand font) at the DOCUMENT level. This is the
// same fontshare stylesheet the main app loads in its index.html. Crucially,
// @font-face rules defined *inside a shadow root are not applied* by browsers —
// font faces must be registered on the document, from where they propagate
// into shadow DOM (the widget's :host sets font-family: "Satoshi"). We also
// drop an @import inside the shadow scope as a harmless fallback.
// Idempotent — if the customer page (or a prior mount) already loads Satoshi,
// we don't duplicate the <link>.
function injectSatoshiFontInto(shadow: ShadowRoot) {
  const fontUrl = "https://api.fontshare.com/v2/css?f[]=satoshi&display=swap"

  const already = document.querySelector(
    'link[data-heykoala-satoshi], link[href*="fontshare.com"][href*="satoshi"]'
  )
  if (!already) {
    const preconnect = document.createElement("link")
    preconnect.rel = "preconnect"
    preconnect.href = "https://api.fontshare.com"
    preconnect.crossOrigin = ""
    document.head.appendChild(preconnect)

    const link = document.createElement("link")
    link.rel = "stylesheet"
    link.href = fontUrl
    link.setAttribute("data-heykoala-satoshi", "")
    document.head.appendChild(link)
  }

  // Belt-and-suspenders: also register inside the shadow scope. Harmless when
  // the document-level load succeeds; helps in edge cases where the host page
  // strips document-injected <link>s.
  const style = document.createElement("style")
  style.textContent = `@import url("${fontUrl}");`
  shadow.appendChild(style)
}

async function mount(mc: MountConfig) {
  let cfg: WidgetConfig
  try {
    cfg = await fetchWidgetConfig(mc.apiBase, mc.publicKey)
  } catch (e) {
    console.warn("HeyKoala widget: failed to load config", e)
    return
  }

  // `mode` ONLY gates the built-in launcher. The window.HeyKoalaVoice API is
  // already live regardless (assigned at load, below). In headless mode we
  // simply don't render our own UI — the host drives the call itself.
  if (cfg.mode === "headless") {
    console.info(
      "[HeyKoala] headless mode — built-in UI not rendered; " +
        "use window.HeyKoalaVoice.start() to begin a call"
    )
    return
  }

  const host = document.createElement("div")
  host.setAttribute("data-heykoala-widget", mc.publicKey)
  host.style.all = "initial"
  document.body.appendChild(host)

  const shadow = host.attachShadow({ mode: "open" })

  // Font loaders go *before* the main stylesheet so the @font-face is
  // registered (and the network request fires) before any rule that
  // references "Satoshi" is applied.
  injectSatoshiFontInto(shadow)

  const styleEl = document.createElement("style")
  styleEl.textContent = styles
  shadow.appendChild(styleEl)

  mountWidget(shadow, cfg, mc)
}

window.HeyKoalaWidget = {
  mount: mc => {
    void mount(mc)
  },
}

// Headless SDK entry point — host page calls this from its own button click.
window.HeyKoalaVoice = {
  start: (options: StartOptions): VoiceCall => {
    const opts: VoiceCallOptions = {
      publicKey: options.publicKey,
      apiBase: options.apiBase ?? resolveApiBase(),
      sessionId: options.sessionId,
      autoMic: options.autoMic,
    }
    const call = new VoiceCall(opts)
    void call.start()
    return call
  },
  getToken: (options: StartOptions): Promise<ProvisionResult> =>
    provisionCall({
      publicKey: options.publicKey,
      apiBase: options.apiBase ?? resolveApiBase(),
      sessionId: options.sessionId,
    }),
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    const cfg = readScriptConfig()
    if (cfg) void mount(cfg)
  })
} else {
  const cfg = readScriptConfig()
  if (cfg) void mount(cfg)
}
